import React from "react";
import { registerView, ViewComponent } from "brew-js-react";
import { extend, matchWord } from "zeta-dom/util";
import type { UserContext } from "src/core/auth";
import { ReminderIDs } from "src/core/constants";
import { ReminderIDMap } from "src/util/routeUtil";

export const Login = registerView(
    () => import("./Login"),
    { view: 'login' });

export const ResetPassword = registerView(
    () => import("./ResetPassword"),
    { view: 'reset-password' });

export const Home = extend(registerView(
    () => import("./Home"),
    { view: 'home' }),
    {
        LOUSupplementalAgreement: registerView(
            () => import("./Home/LOUSupplementalAgreement"),
            { section: 'reminders', view: 'lou-supplemental' }
        )
    }
);

export const LOUSupplementalAgreement = registerView(
    () => import("./Home/LOUSupplementalAgreement"),
    { view: 'lou-supplemental' });

export const HomeSearch = registerView(
    () => import("./Home"),
    { view: 'homesearch' });

export const HomePin = registerView(
    () => import("./Home"),
    { view: 'homepin' });

export const HomeNotice = registerView(
    () => import("./Home"),
    { view: 'homenotice' });

export const Transfer = registerView(
    () => import("./Transfer"),
    { view: 'transfer' });

export const IHSTransfer = registerView(
    () => import("./IHSTransfer"),
    { view: 'ihstransfer' });

export const reportPdf = registerView(
    () => import("./MyPerformance/PDFViewer"),
    { view: 'report-pdf' });

export const MyPerformance = extend(registerView(
    () => import("./MyPerformance"),
    { view: 'my-performance' }),
    {
        CampaignDetail: registerView(
            () => import("./MyPerformance/CampaignDetail"),
            { view: 'my-performance', campaignCode: /./ }),
        CampaignListing: registerView(
            () => import("./MyPerformance/CampaignListing"),
            { view: 'my-performance', campaignCategory: /./ }),
        CommissionPaymentReport: registerView(
            () => import("./MyPerformance/CommissionPaymentReport"),
            { view: 'my-performance', reportType: 'commission-payment' }),
        CustomerContactReport: registerView(
            () => import("./MyPerformance/CustomerContactReport"),
            { view: 'my-performance', reportType: 'customer-contact-report' }),
        TaxableIncomeReport: registerView(
            () => import("./MyPerformance/TaxableIncomeReport"),
            { view: 'my-performance', reportType: 'taxable-income' }),
        AgencyFollowUpReport: registerView(
            () => import("./MyPerformance/AgencyFollowUpReport"),
            { view: 'my-performance', reportType: 'agency-followup-report' }),
        AgencyProgressReport: registerView(
            () => import("./MyPerformance/AgencyProgressReport"),
            { view: 'my-performance', reportType: 'agency-progress' }),
        Eb3Report: registerView(
            () => import("./MyPerformance/Eb3Report"),
            { view: 'my-performance', reportType: 'eb3' }),
        Eb8Report: registerView(
            () => import("./MyPerformance/Eb3Report"),
            { view: 'my-performance', reportType: 'eb8' }),
        MyResultList: registerView(
            () => import("./MyPerformance/MyResultList"),
            { view: "my-performance", reportType: "my-result-list", }),
        ContestsList: registerView(
            () => import("./MyPerformance/ContestsList"),
            { view: 'my-performance', reportType: 'contests-list' }),
        ConventionsList: registerView(
            () => import("./MyPerformance/ConventionsList"),
            { view: 'my-performance', reportType: 'conventions-list' }),
        ProductionReportsList: registerView(
            () => import("./MyPerformance/ProductionReportsList"),
            { view: 'my-performance', reportType: 'production-reports-list' }),
        ReportSelect: registerView(
            () => import("./MyPerformance/ReportSelect"),
            { view: 'my-performance', reportType: 'report-select' }),
        UdrReport: registerView(
            () => import("./MyPerformance/UdrReport"),
            { view: 'my-performance', reportType: 'udr-report' }),
        BasicOverrideReport: registerView(
            () => import("./MyPerformance/BasicOverrideReport"),
            { view: 'my-performance', reportType: 'basic-override' }),
        AdorReport: registerView(
            () => import("./MyPerformance/AdorReport"),
            { view: 'my-performance', reportType: 'agency-development-override' }),
        AmorReport: registerView(
            () => import("./MyPerformance/AmorReport"),
            { view: 'my-performance', reportType: 'agency-management-override' }),
        PfccReport: registerView(
            () => import("./MyPerformance/PfccReport"),
            { view: 'my-performance', reportType: 'pfund-contribution' }),
        TqcpReport: registerView(
            () => import("./MyPerformance/TqcpReport"),
            { view: 'my-performance', reportType: 'tqc-payment' }),
        TqctReport: registerView(
            () => import("./MyPerformance/TqctReport"),
            { view: 'my-performance', reportType: 'tqc-transaction' }),
        Limar19AgencyReport: registerView(
            () => import("./MyPerformance/Limar19AgencyReport"),
            { view: 'my-performance', reportType: 'limra19-agency' }),
        Limar19PersonalReport: registerView(
            () => import("./MyPerformance/Limar19PersonalReport"),
            { view: 'my-performance', reportType: 'limra19-personal' }),
        CtdAgencyReport: registerView(
            () => import("./MyPerformance/CtdAgencyReport"),
            { view: 'my-performance', reportType: 'contract-to-date-agency' }),
        CtdPersonalReport: registerView(
            () => import("./MyPerformance/CtdPersonalReport"),
            { view: 'my-performance', reportType: 'contract-to-date-personal' }),
        YebpReport: registerView(
            () => import("./MyPerformance/YebpReport"),
            { view: 'my-performance', reportType: 'year-end-bonus' }),
        CbpReport: registerView(
            () => import("./MyPerformance/CbpReport"),
            { view: 'my-performance', reportType: 'career-benefit' }),
        PbpReport: registerView(
            () => import("./MyPerformance/PbpReport"),
            { view: 'my-performance', reportType: 'persistency-bonus' }),
    });

export const Products = registerView(
    () => import("./Products"),
    { view: 'products' });

// view是标题，在navigation.ts里接收配置数据
export const Electronic = registerView(
    () => import("./Electronic"),
    { view: 'electronic' });

export const Information = registerView(
    () => import("./Information"),
    { view: 'information' });

export const Forms = registerView(
    () => import("./Forms"),
    { view: 'forms' });

export const Common = registerView(
    () => import("./Common"),
    { view: 'common' });

export const Manuals = registerView(
    () => import("./Manuals"),
    { view: 'manuals' });

export const Software = registerView(
    () => import("./Software"),
    { view: 'software' });

export const Settings = extend(registerView(
    () => import("./Settings"),
    { view: 'settings' }),
    {
        ChangePassword: registerView(
            () => import("./Settings/ChangePassword"),
            { section: 'settings', view: 'change-password' }),
        ResetEmailPassword: registerView(
            () => import("./Settings/ResetEmailPassword"),
            { section: 'settings', view: 'reset-email-password' }),
    });

export const Bulletin = {
    CeoBulletin: registerView(
        () => import("./Bulletin/CeoBulletin"),
        { section: 'bulletin', view: 'ceo-bulletin' }),
    EMemo: registerView(
        () => import("./Bulletin/EMemo"),
        { section: 'bulletin', view: 'ememo' })
};

export const Logout = registerView(
    () => Promise.resolve({ default: () => React.createElement(React.Fragment) }),
    { view: 'logout' });

export const ChangePassword = registerView(
    () => import("./Settings/ChangePassword"),
    { view: 'change-password' });

export const Blocking = registerView(
    () => import("./Blocking"),
    { view: 'before-start' });

export const EService = {
    LicenseInformation: registerView(
        () => import("./EService/LicenseInformation"),
        { section: 'e-service', view: 'license-information' }),
    RestrictionCodeReport: registerView(
        () => import("./EService/RestrictionCodeReport"),
        { section: 'e-service', view: 'restriction-code-report' }),
    ForeverLove: registerView(
        () => import("./EService/ForeverLove"),
        { section: 'e-service', view: 'forever-love' }),
    PremiumHolidayPolicyList: registerView(
        () => import("./EService/PremiumHolidayPolicyList"),
        { section: 'e-service', view: 'premium-holiday-policy-list' }),
    MyCustomer: registerView(
        () => import("./EService/MyCustomer"),
        { section: 'e-service', view: 'my-customer' }),
    MyCustomerDetail: registerView(
        () => import("./EService/MyCustomerDetail"),
        { section: 'e-service', view: 'my-customer', reportName: /./ }),
    EquiryFYCProductGroup: registerView(
        () => import("./EService/EnquiryFYCProductGroup"),
        { section: 'e-service', view: 'enquiry-fyc-product-group' }),
    PremiumPayingRatio: registerView(
        () => import("./EService/PremiumPayingRatio"),
        { section: 'e-service', view: 'premium-paying-ratio' }),
};

export const Reminders = {
    CareerRepresentativeContract: registerView(
        () => import("./Reminders/CareerRepresentativeContract"),
        { section: 'reminders', view: ReminderIDMap.toRouteParam(ReminderIDs.careerRepresentativeContracts)! }),
    LicenseExpiry: registerView(
        () => import("./Reminders/LicenseExpiry"),
        { section: 'reminders', view: ReminderIDMap.toRouteParam(ReminderIDs.iaLicenseRenewal)! }),
    BlueCrossLicenseRequest: registerView(
        () => import("./Reminders/BlueCrossLicenseRequest"),
        { section: 'reminders', view: ReminderIDMap.toRouteParam(ReminderIDs.blueCrossLicenseRequest)! }),
};

export const Departments = {
    FacilitiesManagement: registerView(
        () => import("./Departments/FacilitiesManagement"),
        { section: 'departments', view: 'facilities-management' }),
};

export function getAccessibleViews(currentUser: UserContext): ViewComponent<unknown>[] {
    const { agentType, sCodeType, userType, staffLevel } = currentUser;
    const views = new Set([
        Home,
        LOUSupplementalAgreement,
        Transfer,
        IHSTransfer,
        reportPdf,
        MyPerformance,
        Products,
        Forms,
        Common,
        Manuals,
        Software,
        Settings,
        ChangePassword,
        Electronic,
        Information,
        HomeSearch,
        HomePin,
        HomeNotice,
        ...Object.values(Home),
        ...Object.values(MyPerformance),
        ...Object.values(Bulletin),
        ...Object.values(EService),
        ...Object.values(Settings),
        ...Object.values(Reminders),
        ...Object.values(Departments),
    ]);

    // exclude individual page by display conditions
    // NOT DD or DD's Assistant or DD's District Secretary/Agency Secretary/Personal Secretary
    if (agentType !== 'dd' && userType !== 'assistant' && !matchWord(sCodeType, 'DS AS PS')) {
        views.delete(EService.PremiumHolidayPolicyList);
    }
    // NOT DD, District Secretary/Agency Secretary/Personal Secretary
    if (agentType !== 'dd' && !matchWord(sCodeType, 'DS AS PS')) {
        views.delete(MyPerformance.AgencyProgressReport);
    }
    // Assistant, Administration Manager/Training Manager/Recruitment Manager/Receptionist
    if (userType === 'assistant' || matchWord(sCodeType, 'AM TM RM RE')) {
        views.delete(EService.MyCustomer);
        views.delete(EService.MyCustomerDetail);
    }
    // Administration Manager/Training Manager/Recruitment Manager/Receptionist
    if (matchWord(sCodeType, 'AM TM RM RE')) {
        views.delete(MyPerformance.CustomerContactReport);
        views.delete(MyPerformance.AgencyFollowUpReport);
    }
    // Assistant, Administration Manager/Training Manager/Recruitment Manager/Receptionist/District Secretary/Agency Secretary/Personal Secretary
    if (userType === 'assistant' || matchWord(sCodeType, 'AM TM RM RE DS AS PS')) {
        views.delete(MyPerformance.CommissionPaymentReport);
    }
    if (agentType === 'agent') {
        views.delete(MyPerformance.PbpReport);
    }
    if (staffLevel && Number(staffLevel) < 7) {
        views.delete(MyPerformance.YebpReport);
        views.delete(MyPerformance.CbpReport);
        views.delete(MyPerformance.PbpReport);
    }
    if (userType === 'assistant' || matchWord(sCodeType, 'TM RM RE')) {
        views.delete(MyPerformance.Eb3Report);
        views.delete(MyPerformance.Eb8Report);
    }
    if (userType === 'assistant') {
        views.delete(EService.RestrictionCodeReport);
        views.delete(EService.PremiumPayingRatio);
    }
    if (!currentUser.canViewMyPerformancePage) {
        views.delete(MyPerformance);
        Object.values(MyPerformance).forEach(v => views.delete(v));
    }
    if (!currentUser.canViewCampaigns) {
        views.delete(MyPerformance.CampaignListing);
        views.delete(MyPerformance.CampaignDetail);
    }
    return [...views];
}
